import { Fragment, useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import SupplierPoSection from "@/components/SupplierPoSection";
import RFQWorkflow from "@/components/RFQWorkflow";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { OrderDocuments } from "@/components/OrderDocuments";
import { DeliveryWizard, StartDeliveryButton } from "@/components/DeliveryWizard";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Save, Trash2, FileText, Building2, Truck, FileCheck2, Upload, Ship, Send, Clock, Copy, ExternalLink, Sparkles, Paperclip, Download, X } from "lucide-react";

function fmtD(v) { if (!v) return "—"; try { return new Date(v).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; } }

function OrderDeliveries({ orderId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [wiz, setWiz] = useState(false);
  const load = useCallback(() => { api.get(`/orders/${orderId}/deliveries`).then((r) => setRows(r.data)).catch(() => {}); }, [orderId]);
  useEffect(() => { load(); }, [load]);
  const view = async (did, which) => { try { const res = await api.get(`/deliveries/${did}/file/${which}`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" })); window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000); } catch (e) { toast.error(apiError(e)); } };
  const del = async (did) => { if (!window.confirm("Delete This Delivery Record?")) return; try { await api.delete(`/deliveries/${did}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="rounded-lg border border-border bg-slate-50/60 p-3" data-testid="order-section-deliveries">
      <div className="mb-1 flex items-center gap-2">
        <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><FileCheck2 className="h-4 w-4" /></span>
        <h3 className="font-heading text-sm font-bold text-slate-800">Recorded Documents</h3>
      </div>
      <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">Every SOM-DN &amp; INV Uploaded By Logistics Or Here Is Kept Below. Partial Deliveries Are Supported; The Order Completes On The Final One.</p>
      <div className="mb-3"><StartDeliveryButton onClick={() => setWiz(true)} /></div>
      <DeliveryWizard open={wiz} onOpenChange={setWiz} postUrl={`/orders/${orderId}/deliveries`} onDone={() => { load(); onChanged?.(); }} />
      {rows.length ? (
        <div className="space-y-2">
          {rows.map((d) => (
            <div key={d.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border p-3" data-testid={`order-delivery-${d.id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 font-mono text-xs font-bold text-slate-800">{d.title}{d.is_final && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Final</span>}</div>
                <div className="text-[11px] text-muted-foreground">{d.shipment_no ? `${d.shipment_no} · ` : ""}{d.created_by_name} · {fmtD(d.created_at)}{d.note ? ` · ${d.note}` : ""}</div>
              </div>
              <div className="flex items-center gap-1.5">
                {d.has_combined && <button onClick={() => view(d.id, "combined")} className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`order-delivery-combined-${d.id}`}>DN &amp; INV</button>}
                {d.has_dn && <button onClick={() => view(d.id, "dn")} className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`order-delivery-dn-${d.id}`}><FileText className="h-3 w-3" />DN</button>}
                {d.has_inv && <button onClick={() => view(d.id, "inv")} className="inline-flex items-center gap-1 rounded border border-input px-2 py-1 text-[11px] hover:bg-slate-50" data-testid={`order-delivery-inv-${d.id}`}><FileText className="h-3 w-3" />INV</button>}
                <button onClick={() => del(d.id)} className="text-slate-400 hover:text-red-600" data-testid={`order-delivery-del-${d.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
            </div>
          ))}
        </div>
      ) : <div className="rounded-lg border border-dashed border-border p-6 text-center text-[13px] text-muted-foreground" data-testid="order-deliveries-empty">Nothing Recorded Yet. Deliveries From The Shipment's Delivery Tab Show Here Automatically.</div>}
    </div>
  );
}

const money = (n, cur) => `${cur || ""} ${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`.trim();
const titleCase = (s) => (s || "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
const withCommas = (v) => { if (v === "" || v == null) return ""; const [i, d] = String(v).split("."); const fi = i.replace(/\B(?=(\d{3})+(?!\d))/g, ","); return d != null ? `${fi}.${d}` : fi; };

const STATUS_FLOW = ["open", "in_production", "supplier_collected", "in_transit", "delivered", "complete", "cancelled"];
const STATUS_LABEL = { open: "Open", in_production: "In Production", supplier_collected: "Supplier Collected", in_transit: "In Transit", delivered: "Delivered to Customer", complete: "Complete", cancelled: "Cancelled", fulfilled: "Fulfilled", closed: "Closed" };
const STATUS_TINT = { open: "bg-[#E6ECF5] text-[#1B4383]", in_production: "bg-violet-100 text-violet-700", supplier_collected: "bg-sky-100 text-sky-700", in_transit: "bg-indigo-100 text-indigo-700", delivered: "bg-teal-100 text-teal-700", complete: "bg-emerald-100 text-emerald-700", cancelled: "bg-slate-200 text-slate-500", fulfilled: "bg-emerald-100 text-emerald-700", closed: "bg-slate-100 text-slate-600" };
const CRIT = { low: "bg-emerald-100 text-emerald-700", medium: "bg-amber-100 text-amber-700", high: "bg-red-100 text-red-700" };

function FileSlot({ orderId, slot, label, hint, has, filename, onChanged }) {
  const [busy, setBusy] = useState(false);
  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); e.target.value = ""; return; }
    setBusy(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      await api.put(`/orders/${orderId}/doc-file`, { slot, file: b64, filename: f.name, content_type: f.type || "application/octet-stream" });
      toast.success(`${label} uploaded`); onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); e.target.value = ""; }
  };
  const open = async () => {
    try { const res = await api.get(`/orders/${orderId}/doc-file/${slot}`, { responseType: "blob" }); const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" })); window.open(url, "_blank"); setTimeout(() => window.URL.revokeObjectURL(url), 60000); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async () => { try { await api.delete(`/orders/${orderId}/doc-file/${slot}`); onChanged(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-slate-50/60 p-3 sm:flex-row sm:items-center sm:justify-between" data-testid={`file-slot-${slot}`}>
      <div className="min-w-0">
        <div className="text-sm font-semibold text-slate-800">{label}{has && <span className="ml-2 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700"><FileCheck2 className="h-3 w-3" />Scanned</span>}</div>
        <div className="truncate text-[11px] text-muted-foreground">{has ? filename : hint}</div>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        {has && <Button variant="outline" size="sm" onClick={open} data-testid={`file-open-${slot}`} className="border-[#1B4383]/30 text-[#1B4383] hover:bg-[#E6ECF5]"><FileText className="mr-1.5 h-3.5 w-3.5" />View</Button>}
        {has && <button onClick={remove} className="text-slate-400 hover:text-red-600" data-testid={`file-del-${slot}`}><Trash2 className="h-4 w-4" /></button>}
        <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-input bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50" data-testid={`file-upload-${slot}`}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}{has ? "Replace" : "Upload"}
          <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx,.doc,.docx" onChange={upload} />
        </label>
      </div>
    </div>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingPo, setSavingPo] = useState(false);
  const [busy, setBusy] = useState("");
  const [uploadingPo, setUploadingPo] = useState(false);
  const [cpo, setCpo] = useState({ client_po_ref: "", client_po_date: "", client_po_sent_date: "", po_description: "", po_value: "", po_cost: "", po_currency: "TZS", delivery_date: "" });
  const location = useLocation();
  const ackAuto = useRef(false);
  const [ackOpen, setAckOpen] = useState(false);
  const [ackForm, setAckForm] = useState({ subject: "", message: "" });
  const [spos, setSpos] = useState([]);
  const [poScan, setPoScan] = useState({ type: "pdf", text: "", file: null });
  const [scanBusy, setScanBusy] = useState(false);
  const [repairing, setRepairing] = useState(false);
  const fmtNum = (n) => (n === null || n === undefined || n === "" ? "—" : Number(n).toLocaleString());

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/orders/${id}`);
      setOrder(data);
      setCpo({ client_po_ref: data.client_po_ref || "", client_po_date: data.client_po_date || "", client_po_sent_date: data.client_po_sent_date || "", po_description: data.po_description || "", po_value: data.po_value ?? "", po_cost: data.po_cost ?? "", po_currency: data.po_currency === "TSh" || !data.po_currency ? "TZS" : data.po_currency, delivery_date: data.delivery_date || "" });
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const openAck = useCallback(async () => {
    try { const { data } = await api.get(`/orders/${id}/acknowledge/draft`); setAckForm({ subject: data.subject || "", message: data.message || "" }); setAckOpen(true); }
    catch (e) { toast.error(apiError(e)); }
  }, [id]);
  const copyAck = async () => {
    try { await navigator.clipboard.writeText(`Subject: ${ackForm.subject}\n\n${ackForm.message}`); toast.success("PO acknowledgment draft copied to clipboard"); setAckOpen(false); }
    catch (e) { toast.error("Couldn't copy — select the text manually"); }
  };
  useEffect(() => {
    if (order && location.state?.ackPo && !ackAuto.current) {
      ackAuto.current = true;
      openAck();
      nav(location.pathname, { replace: true, state: {} });
    }
  }, [order, location, nav, openAck]);
  useEffect(() => { api.get(`/orders/${id}/supplier-pos`).then((r) => setSpos(r.data)).catch(() => {}); }, [id]);

  const delSpo = async (c) => {
    const issued = c.request?.status === "issued";
    const label = (issued && c.som_po_no) ? c.som_po_no : (c.supplier_name || "this Somcore PO");
    const msg = issued
      ? `⚠️ ${label} has already been ISSUED to the supplier. Deleting it will remove the Somcore PO record. Delete anyway?`
      : `Delete Somcore PO for ${label}? This cannot be undone.`;
    if (!window.confirm(msg)) return;
    try {
      await api.delete(`/orders/${id}/supplier-pos/${c.id}`);
      setSpos((p) => p.filter((x) => x.id !== c.id));
      toast.success("Somcore PO deleted");
      try {
        const { data } = await api.get("/shipments");
        const ship = (data || []).find((s) => s.order_id === id);
        if (ship && window.confirm(`This order has a linked shipment ${ship.shipment_no}. Delete that shipment too?`)) {
          await api.delete(`/shipments/${ship.id}`);
          toast.success("Shipment deleted");
        }
      } catch (_) { /* best-effort */ }
    }
    catch (e) { toast.error(apiError(e)); }
  };

  const saveClientPo = async () => {
    setSavingPo(true);
    try {
      await api.put(`/orders/${id}`, { ...cpo, po_value: cpo.po_value === "" ? null : Number(cpo.po_value), po_cost: cpo.po_cost === "" ? null : Number(cpo.po_cost) });
      toast.success("Client PO saved"); load();
    } catch (e) { toast.error(apiError(e)); } finally { setSavingPo(false); }
  };
  const uploadClientPo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error("File must be under 15 MB"); return; }
    setUploadingPo(true);
    try {
      const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(f); });
      await api.put(`/orders/${id}`, { client_po_file: b64, client_po_filename: f.name, client_po_content_type: f.type || "application/octet-stream" });
      toast.success("Client PO file stored"); load();
    } catch (err) { toast.error(apiError(err)); } finally { setUploadingPo(false); e.target.value = ""; }
  };
  const openClientPo = async () => {
    try {
      const res = await api.get(`/orders/${id}/client-po/file`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: res.headers["content-type"] || "application/pdf" })); window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteClientPo = async () => {
    if (!window.confirm("Remove the stored customer PO file?")) return;
    try { await api.put(`/orders/${id}`, { client_po_file: null, client_po_filename: "", client_po_content_type: "" }); toast.success("PO file removed"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const extractMatch = async (useStored = false) => {
    setScanBusy(true);
    try {
      let body;
      if (useStored) {
        body = { use_stored: true };
      } else {
        body = { source_type: poScan.type };
        if (poScan.type === "text") {
          if (!poScan.text.trim()) { toast.error("Paste the PO text first"); setScanBusy(false); return; }
          body.text = poScan.text;
        } else {
          if (!poScan.file) { toast.error("Choose a file first"); setScanBusy(false); return; }
          const b64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(String(r.result).split(",")[1] || ""); r.onerror = rej; r.readAsDataURL(poScan.file); });
          body.file_base64 = b64;
        }
      }
      const { data } = await api.post(`/orders/${id}/po-extract-match`, body);
      const ok = data.matches.filter((m) => m.matched).length;
      toast.success(`Scanned — ${ok}/${data.matches.length} quote lines matched, ${data.extras.length} extra`);
      load();
    } catch (e) { toast.error(apiError(e)); } finally { setScanBusy(false); }
  };

  const repairPair = async (quoteIdx, poIdx) => {
    const pm = order.po_match; if (!pm) return;
    const pairing = pm.matches.map((m) => (m.po_index != null ? m.po_index : null));
    if (poIdx !== null) pairing.forEach((v, i) => { if (v === poIdx) pairing[i] = null; });
    pairing[quoteIdx] = poIdx;
    setRepairing(true);
    try { await api.post(`/orders/${id}/po-repair`, { pairing }); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setRepairing(false); }
  };

  const genDoc = async (doc_type, supplier_id) => {
    setBusy(doc_type);
    try { await api.post(`/orders/${id}/docs`, { doc_type, supplier_id }); toast.success("Document generated"); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(""); }
  };
  const patch = async (fields, msg) => {
    try { await api.put(`/orders/${id}`, fields); if (msg) toast.success(msg); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const setStatus = (status) => patch({ status }, `Order → ${STATUS_LABEL[status]}`);
  const setCrit = (criticality) => patch({ criticality });
  const linkLogistics = async () => {
    try {
      const { data } = await api.post(`/orders/${id}/shipment`);
      toast.success(`Tracking ${data.shipment_no}`);
      nav(`/logistics/shipment/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  const deleteOrder = async () => {
    if (!window.confirm(`Delete order ${order.order_no}? This cannot be undone.`)) return;
    try { await api.delete(`/orders/${id}`); toast.success("Order deleted"); nav("/orders"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (loading || !order) return <AppLayout title="Order"><div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div></AppLayout>;

  const crit = order.criticality || "medium";

  const scrollTop = () => window.scrollTo({ top: 0, behavior: "smooth" });
  const scrollBottom = () => window.scrollTo({ top: document.body.scrollHeight, behavior: "smooth" });
  const orderWf = [
    { key: "client-po", label: "Client PO", done: !!(order.client_po_ref || order.has_client_po_file), onClick: scrollTop },
    { key: "supplier-po", label: "Supplier POs", done: spos.some((c) => c.request?.status === "issued"), onClick: scrollBottom },
    { key: "shipment", label: "Shipment", done: !!order.logistics_ref, onClick: linkLogistics },
    { key: "delivery", label: "DN & Invoice", done: ["delivered", "complete", "fulfilled", "closed"].includes(order.status), onClick: scrollBottom },
    { key: "complete", label: "Complete", done: ["complete", "fulfilled", "closed"].includes(order.status), onClick: scrollTop },
  ];
  const orderWfCurrent = orderWf.findIndex((s) => !s.done);

  return (
    <AppLayout title={order.order_no}>
      <button onClick={() => nav("/orders")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-slate-800" data-testid="back-orders"><ArrowLeft className="h-4 w-4" />Back to Customer PO's</button>

      <div className="mb-6 rounded-lg border border-border bg-white p-5" data-testid="order-header">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="grid w-full grid-cols-2 gap-x-6 gap-y-3 text-sm sm:gap-x-10 md:w-auto md:grid-cols-4">
            <Meta label="Sales Order" value={order.order_no} mono />
            <div>
              <div className="text-xs tracking-wide text-muted-foreground">Client</div>
              <div className="mt-0.5 font-medium text-slate-900" data-testid="order-client-name">{titleCase(order.client_name) || "—"}</div>
            </div>
            <Meta label="Client PO" value={order.client_po_ref || "—"} />
            <div>
              <div className="text-xs tracking-wide text-muted-foreground">Won RFQ{(order.rfq_ids || []).length > 1 ? `s (${order.rfq_ids.length})` : ""}</div>
              <div className="mt-0.5 flex flex-col gap-0.5" data-testid="order-rfq-links">
                {(order.rfq_ids?.length ? order.rfq_ids : [order.rfq_id]).filter(Boolean).map((rid, i) => (
                  <Link key={rid} to={`/rfqs/${rid}`} data-testid={i === 0 ? "order-rfq-link" : `order-rfq-link-${i}`} className="inline-flex items-center gap-1 font-medium text-[#1B4383] underline-offset-2 hover:underline">{order.rfq_codes?.[i] || order.rfq_code}<ExternalLink className="h-3.5 w-3.5 opacity-70" /></Link>
                ))}
              </div>
            </div>
            <Meta label="Created" value={fmtD(order.created_at)} mono />
            <Meta label="Delivery Date" value={order.delivery_date ? fmtD(order.delivery_date) : "—"} mono />
            <div>
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">Criticality</div>
              <Select value={crit} onValueChange={setCrit}>
                <SelectTrigger className={`h-7 w-auto gap-1 rounded-full border-0 px-2.5 text-[11px] font-semibold focus:ring-0 ${CRIT[crit]}`} data-testid="order-criticality"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low</SelectItem>
                  <SelectItem value="medium">Medium</SelectItem>
                  <SelectItem value="high">High</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">Order / Shipment</div>
              <div className="mt-0.5 flex flex-wrap items-center gap-2">
                <button type="button" onClick={linkLogistics} data-testid="order-logistics-btn" className="inline-flex items-center gap-1.5 font-medium text-[#1B4383] underline-offset-2 hover:underline">
                  <Ship className="h-3.5 w-3.5" />{order.logistics_ref || "Track Shipment"}
                </button>
                <button type="button" onClick={openAck} data-testid="order-ack-po-btn" className="inline-flex items-center gap-1 rounded-md border border-[#E0B84B] bg-[#FFFBEB] px-2 py-0.5 text-xs font-semibold text-[#8A6D1F] hover:bg-[#FBF3D9]">
                  <Copy className="h-3.5 w-3.5" />Acknowledge PO
                </button>
              </div>
            </div>
            <div className="col-span-2 md:col-span-4">
              <div className="mb-1 text-xs tracking-wide text-muted-foreground">Supplier POs (Somcore POs)</div>
              {spos.length ? (
                <div className="mt-0.5 flex flex-wrap items-center gap-1.5" data-testid="order-supplier-pos">
                  {spos.map((c) => {
                    const issued = c.request?.status === "issued";
                    return (
                      <span key={c.id} data-testid={`order-spo-chip-${c.id}`}
                        className={`inline-flex items-center rounded-md border text-xs font-medium ${issued ? "border-[#1B4383]/30 bg-[#E6ECF5] text-[#1B4383]" : "border-border bg-slate-50 text-slate-500"}`}>
                        <Link to={`/orders/som-po/${c.id}`} data-testid={`order-spo-link-${c.id}`} className="inline-flex items-center gap-1 py-0.5 pl-2 underline-offset-2 hover:underline">
                          {issued && c.som_po_no ? c.som_po_no : (c.supplier_name || "Supplier PO")}
                          {!issued && <span className="text-[10px] opacity-70">· pending</span>}
                          <ExternalLink className="h-3 w-3 opacity-70" />
                        </Link>
                        <button onClick={() => delSpo(c)} title="Delete Somcore PO" data-testid={`order-spo-del-${c.id}`} className="ml-0.5 rounded-r-md py-0.5 pl-1 pr-1.5 text-current/60 hover:bg-red-50 hover:text-red-600"><X className="h-3 w-3" /></button>
                      </span>
                    );
                  })}
                </div>
              ) : <div className="mt-0.5 text-sm text-muted-foreground">—</div>}
            </div>
          </div>
          <div>
            <div className="mb-2 text-xs tracking-wide text-muted-foreground md:text-right">Order Status</div>
            <div className="flex flex-wrap items-center gap-2 md:justify-end" data-testid="order-status-flow" role="group" aria-label="Order status">
              {STATUS_FLOW.map((s) => (
                <button key={s} onClick={() => setStatus(s)} data-testid={`order-status-${s}`} aria-current={order.status === s ? "step" : undefined}
                  className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${order.status === s ? "bg-[#1B4383] text-white shadow-sm shadow-[#1B4383]/30" : "bg-slate-100 text-slate-500 hover:bg-slate-200"}`}>
                  {STATUS_LABEL[s]}
                </button>
              ))}
            </div>
          </div>
        </div>

        <h2 className="mt-4 font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl" data-testid="order-title">{order.po_description || order.rfq_title || order.order_no}</h2>

        <div className="mt-4 flex flex-wrap items-center gap-x-8 gap-y-3 rounded-xl border border-border bg-slate-50/70 px-5 py-3.5" data-testid="order-metrics">
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-slate-400">Status</div>
            <span className={`mt-0.5 inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-semibold ${STATUS_TINT[order.status] || STATUS_TINT.open}`} data-testid="order-status-pill"><span className="h-1.5 w-1.5 rounded-full bg-current" />{STATUS_LABEL[order.status] || order.status}</span>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-slate-400">PO Value</div>
            <div className="mt-0.5 font-heading text-lg font-bold text-slate-900" data-testid="order-value">{order.po_value != null ? money(order.po_value, order.po_currency) : "—"}</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-slate-400">Margin</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-700" data-testid="order-margin-pill">{Number(order.gp_pct).toFixed(1)}%</div>
          </div>
          <div>
            <div className="text-[11px] font-semibold tracking-wide text-slate-400">Net Profit</div>
            <div className="mt-0.5 text-lg font-semibold text-slate-700" data-testid="order-net-profit-pill">{money(order.gp, order.po_currency)}</div>
          </div>
          <div className="ml-auto">
            <Button variant="outline" size="sm" onClick={deleteOrder} data-testid="order-delete-btn" className="border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"><Trash2 className="mr-1.5 h-4 w-4" />Delete</Button>
          </div>
        </div>
      </div>

      <RFQWorkflow steps={orderWf} currentIndex={orderWfCurrent} title="Order Workflow" />

      {/* Step 1: Client PO capture */}
      <div className="mb-6 rounded-xl border border-border bg-white p-5" data-testid="client-po-card">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E4EEE8] text-[#527D63]"><FileCheck2 className="h-4 w-4" /></span>
          <h3 className="font-heading text-sm font-bold text-slate-800">① Master Customer PO</h3>
        </div>
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-1.5"><Label className="text-xs">PO Number</Label><Input value={cpo.client_po_ref} onChange={(e) => setCpo({ ...cpo, client_po_ref: e.target.value })} placeholder="e.g. PO-4523" data-testid="cpo-ref" /></div>
              <div className="space-y-1.5"><Label className="text-xs">PO Issued Date</Label><Input type="date" value={cpo.client_po_date} onChange={(e) => setCpo({ ...cpo, client_po_date: e.target.value })} data-testid="cpo-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Date Client Sent PO</Label><Input type="date" value={cpo.client_po_sent_date} onChange={(e) => setCpo({ ...cpo, client_po_sent_date: e.target.value })} data-testid="cpo-sent-date" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Delivery Date</Label><Input type="date" value={cpo.delivery_date} onChange={(e) => setCpo({ ...cpo, delivery_date: e.target.value })} data-testid="cpo-delivery" /></div>
              <div className="space-y-1.5 sm:col-span-2"><Label className="text-xs">Description</Label><Input value={cpo.po_description} onChange={(e) => setCpo({ ...cpo, po_description: e.target.value })} placeholder="What the PO is for (shows as the order title)" data-testid="cpo-desc" /></div>
              <div className="space-y-1.5">
                <Label className="text-xs">PO Value (selling)</Label>
                <div className="flex gap-2">
                  <Select value={cpo.po_currency} onValueChange={(v) => setCpo({ ...cpo, po_currency: v })}>
                    <SelectTrigger className="w-24" data-testid="cpo-cur"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="TZS">TZS</SelectItem><SelectItem value="USD">USD</SelectItem></SelectContent>
                  </Select>
                  <Input inputMode="decimal" value={withCommas(cpo.po_value)} onChange={(e) => setCpo({ ...cpo, po_value: e.target.value.replace(/,/g, "") })} placeholder="0.00" data-testid="cpo-value" />
                </div>
                {order.po_match?.quote_total != null && (
                  <button type="button" onClick={() => setCpo({ ...cpo, po_value: String(order.po_match.quote_total), po_currency: order.po_match.quote_currency || cpo.po_currency })} className="text-[11px] font-medium text-[#1B4383] hover:underline" data-testid="cpo-use-quote-total">Use matched quote total: {order.po_match.quote_currency || "TZS"} {Number(order.po_match.quote_total).toLocaleString()}</button>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Cost ({cpo.po_currency})</Label>
                <Input inputMode="decimal" value={withCommas(cpo.po_cost)} onChange={(e) => setCpo({ ...cpo, po_cost: e.target.value.replace(/,/g, "") })} placeholder="0.00" data-testid="cpo-cost" />
                {order.auto_cost_tzs != null && cpo.po_currency === "TZS" && (
                  <button type="button" onClick={() => setCpo({ ...cpo, po_cost: String(order.auto_cost_tzs) })} className="text-[11px] font-medium text-[#1B4383] hover:underline" data-testid="cpo-use-costing">Use costing cost: TZS {Number(order.auto_cost_tzs).toLocaleString()}</button>
                )}
              </div>
            </div>
            <div className="flex items-center justify-end border-t border-border pt-4">
              <Button onClick={saveClientPo} disabled={savingPo} data-testid="cpo-save" className="bg-[#1B4383] hover:bg-[#16386e]">{savingPo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}Save PO Snapshot</Button>
            </div>
            <p className="text-[11px] text-muted-foreground">Value − Cost drives the Margin / Net Profit shown above. This snapshot + the uploaded PO feed the SOM-DN / SOM-INV references.</p>
          </div>

          <div className="rounded-lg border border-border bg-white p-5" data-testid="cpo-doc-panel">
            <div className="mb-3 flex items-center gap-2">
              <Paperclip className="h-4 w-4 text-primary" />
              <h2 className="font-heading text-base font-bold tracking-tight">Customer PO Document</h2>
            </div>
            <p className="mb-3 text-xs text-muted-foreground">The purchase order issued to Somcore by the client — stored here for reference and AI matching.</p>
            <div className="space-y-2">
              {order.has_client_po_file ? (
                <div className="flex items-center justify-between gap-2 rounded-md border border-border p-2.5" data-testid="cpo-file-row">
                  <button onClick={openClientPo} className="flex min-w-0 items-center gap-2 text-left" data-testid="cpo-file-open">
                    <FileText className="h-4 w-4 shrink-0 text-[#1B4383]" />
                    <span className="min-w-0"><span className="block truncate text-sm font-medium text-slate-800">{order.client_po_filename || "Customer PO"}</span><span className="block text-xs text-muted-foreground">Customer PO</span></span>
                  </button>
                  <div className="flex shrink-0 items-center gap-2">
                    <button onClick={openClientPo} className="text-slate-400 hover:text-[#1B4383]" data-testid="cpo-file-download"><Download className="h-4 w-4" /></button>
                    <button onClick={deleteClientPo} className="text-slate-400 hover:text-destructive" data-testid="cpo-file-delete"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              ) : <p className="rounded-md border border-dashed border-border px-3 py-6 text-center text-xs text-muted-foreground">No PO file stored yet.</p>}
              <label className="block">
                <input type="file" className="hidden" accept=".pdf,.png,.jpg,.jpeg,.xls,.xlsx" onChange={uploadClientPo} data-testid="cpo-file-input" />
                <Button asChild variant="outline" size="sm" className="w-full" disabled={uploadingPo}><span>{uploadingPo ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}{order.has_client_po_file ? "Replace Document" : "Upload Document"}</span></Button>
              </label>
            </div>

            <div className="mt-5 border-t border-border pt-4">
              <div className="mb-3 flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                <h2 className="font-heading text-base font-bold tracking-tight">Scan &amp; Match with AI</h2>
              </div>
              <p className="mb-3 text-xs text-muted-foreground">Reads the PO and compares each line to what Somcore quoted.</p>
              {order.has_client_po_file && (
                <div className="mb-4 rounded-md border border-[#1B4383]/20 bg-[#EEF3FB] p-3">
                  <Button onClick={() => extractMatch(true)} disabled={scanBusy} data-testid="po-scan-stored" className="bg-[#1B4383] hover:bg-[#16386e]">{scanBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}Scan the Uploaded PO</Button>
                  <p className="mt-1.5 text-[11px] text-muted-foreground">Uses the Customer PO document above — no need to upload it again.</p>
                </div>
              )}
              {order.has_client_po_file && <p className="mb-2 text-[11px] font-medium text-slate-500">Or scan a different file:</p>}
              <Tabs value={poScan.type} onValueChange={(v) => setPoScan({ type: v, text: "", file: null })}>
                <TabsList className="flex h-auto flex-wrap justify-start gap-1" data-testid="po-scan-tabs">
                  <TabsTrigger value="text" data-testid="po-scan-type-text">Paste Text</TabsTrigger>
                  <TabsTrigger value="pdf" data-testid="po-scan-type-pdf">Upload PDF</TabsTrigger>
                  <TabsTrigger value="image" data-testid="po-scan-type-image">Upload Image</TabsTrigger>
                  <TabsTrigger value="excel" data-testid="po-scan-type-excel">Upload Excel</TabsTrigger>
                </TabsList>
                <TabsContent value="text" className="mt-4"><Textarea rows={5} value={poScan.text} onChange={(e) => setPoScan({ ...poScan, text: e.target.value })} placeholder="Paste the customer PO text here…" data-testid="po-scan-text" /></TabsContent>
                <TabsContent value="pdf" className="mt-4"><Input type="file" accept="application/pdf" onChange={(e) => setPoScan({ ...poScan, file: e.target.files?.[0] || null })} data-testid="po-scan-file-pdf" /></TabsContent>
                <TabsContent value="image" className="mt-4"><Input type="file" accept="image/*" onChange={(e) => setPoScan({ ...poScan, file: e.target.files?.[0] || null })} data-testid="po-scan-file-image" /></TabsContent>
                <TabsContent value="excel" className="mt-4"><Input type="file" accept=".xlsx,.xls" onChange={(e) => setPoScan({ ...poScan, file: e.target.files?.[0] || null })} data-testid="po-scan-file-excel" /></TabsContent>
              </Tabs>
              <div className="mt-4">
                <Button onClick={() => extractMatch(false)} disabled={scanBusy} data-testid="po-scan-run">{scanBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{scanBusy ? "Scanning..." : "Extract & Match with AI"}</Button>
              </div>
            </div>
          </div>
        </div>

        {order.po_match && (
          <div className="mt-5 border-t border-border pt-4" data-testid="po-match-results">
            <div className="mb-2 text-xs font-semibold text-slate-700">PO vs Our Quote{order.po_match_at ? <span className="font-normal text-muted-foreground"> · last scanned {fmtD(order.po_match_at)}</span> : ""}</div>
            {(order.po_match.po_lines && order.po_match.po_lines.length) ? <p className="mb-2 text-[11px] text-muted-foreground">The AI made a first guess — use the dropdown in each row to correct which PO line it pairs with.</p> : null}
            <div className="overflow-x-auto rounded-lg border border-border">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500"><tr>
                  <th className="px-3 py-2 text-left font-semibold">Our Quote Line</th>
                  <th className="px-3 py-2 text-left font-semibold">On Customer PO</th>
                  <th className="px-3 py-2 text-right font-semibold">Qty (Quote / PO)</th>
                  <th className="px-3 py-2 text-right font-semibold">Unit Price (Quote / PO)</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                </tr></thead>
                <tbody>
                  {order.po_match.matches.map((m, i) => {
                    const ql = order.po_match.quote_lines?.[i] || {};
                    const prev = order.po_match.quote_lines?.[i - 1] || {};
                    const multi = new Set((order.po_match.quote_lines || []).map((q) => q.rfq_code)).size > 1;
                    const group = multi && ql.rfq_code && (i === 0 || prev.rfq_code !== ql.rfq_code);
                    const inGroup = (order.po_match.quote_lines || []).map((q, k) => [q, order.po_match.matches[k]]).filter(([q]) => q.rfq_code === ql.rfq_code);
                    return (<Fragment key={i}>
                    {group && (
                      <tr className="border-t border-border bg-[#EEF3FA]" data-testid={`po-match-group-${ql.rfq_code}`}>
                        <td colSpan={5} className="px-3 py-1.5 text-[11px] font-semibold text-[#1B4383]">From quote {ql.quote_no || ql.rfq_code} · {ql.rfq_code}{ql.rfq_title ? ` · ${ql.rfq_title}` : ""}<span className="ml-2 font-normal text-slate-500">{inGroup.filter(([, mm]) => mm?.matched).length} of {inGroup.length} lines matched</span></td>
                      </tr>
                    )}
                    <tr className="border-t border-border">
                      <td className="px-3 py-2">{m.quote_description}</td>
                      <td className="px-3 py-2">
                        {(order.po_match.po_lines && order.po_match.po_lines.length) ? (
                          <Select value={m.po_index != null ? String(m.po_index) : "none"} onValueChange={(v) => repairPair(i, v === "none" ? null : Number(v))}>
                            <SelectTrigger className="h-auto min-h-8 w-full whitespace-normal py-1 text-xs [&>span]:line-clamp-2" disabled={repairing} data-testid={`po-pair-${i}`}><SelectValue placeholder="— Not on PO —" /></SelectTrigger>
                            <SelectContent className="max-h-72">
                              <SelectItem value="none">— Not on PO —</SelectItem>
                              {(order.po_match.po_lines || []).map((p, pi) => <SelectItem key={pi} value={String(pi)}>{p.description || `PO line ${pi + 1}`}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        ) : (m.matched ? m.po_description : <span className="text-red-500">Not found on PO</span>)}
                      </td>
                      <td className={`px-3 py-2 text-right ${m.matched && !m.qty_match ? "font-semibold text-amber-600" : ""}`}>{fmtNum(m.quote_qty)} / {m.matched ? fmtNum(m.po_qty) : "—"}</td>
                      <td className={`px-3 py-2 text-right ${m.matched && !m.price_match ? "font-semibold text-amber-600" : ""}`}>{fmtNum(m.quote_price)} / {m.matched ? fmtNum(m.po_price) : "—"}</td>
                      <td className="px-3 py-2 text-center">{!m.matched ? <span className="rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-semibold text-red-700">Missing</span> : (m.qty_match && m.price_match ? <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Match</span> : <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Discrepancy</span>)}</td>
                    </tr>
                    </Fragment>);
                  })}
                  {order.po_match.extras.map((x, i) => (
                    <tr key={`x${i}`} className="border-t border-border bg-amber-50/40">
                      <td className="px-3 py-2 italic text-muted-foreground">Not on our quote</td>
                      <td className="px-3 py-2">{x.description}</td>
                      <td className="px-3 py-2 text-right">— / {fmtNum(x.quantity)}</td>
                      <td className="px-3 py-2 text-right">— / {fmtNum(x.unit_price)}</td>
                      <td className="px-3 py-2 text-center"><span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700">Extra</span></td>
                    </tr>
                  ))}
                  {!order.po_match.matches.length && !order.po_match.extras.length && (
                    <tr><td colSpan={5} className="px-3 py-4 text-center text-muted-foreground">No lines detected. Try a clearer file or paste the PO text.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      <Tabs defaultValue="overview" className="space-y-4">
      <TabsList data-testid="order-tabs">
        <TabsTrigger value="overview" data-testid="order-tab-overview">Overview</TabsTrigger>
        <TabsTrigger value="documents" data-testid="order-tab-documents">Documents</TabsTrigger>
      </TabsList>
      <TabsContent value="documents"><OrderDocuments orderId={id} /></TabsContent>
      <TabsContent value="overview">
      <div className="space-y-6">
        <SupplierPoSection orderId={id} />

        <div className="rounded-xl border border-border bg-white p-5" data-testid="order-section-dn_inv">
          <div className="mb-1 flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-[#E6ECF5] text-[#1B4383]"><Truck className="h-4 w-4" /></span>
            <h3 className="font-heading text-sm font-bold text-slate-800">SOM-DN &amp; INV — Delivery Note &amp; Tax Invoice to Client</h3>
          </div>
          <p className="mb-3 text-[11px] leading-relaxed text-muted-foreground">Delivery Notes &amp; Tax Invoices Uploaded By Logistics Appear Below Automatically. Tap Start Delivery To Add One Here.</p>
          <div className="space-y-2.5">
            <OrderDeliveries orderId={id} onChanged={load} />
            {(order.has_dn_inv_file || order.has_dn_file || order.has_inv_file) && (
              <div className="space-y-2.5 border-t border-dashed border-border pt-2.5">
                <p className="text-[10px] font-semibold tracking-wide text-muted-foreground">Previously Uploaded</p>
                {order.has_dn_inv_file && <FileSlot orderId={id} slot="dn_inv" label="Delivery Note & Tax Invoice (SOM-DN & INV)" hint="" has={order.has_dn_inv_file} filename={order.dn_inv_filename} onChanged={load} />}
                {order.has_dn_file && <FileSlot orderId={id} slot="dn" label="Delivery Note (SOM-DN)" hint="" has={order.has_dn_file} filename={order.dn_filename} onChanged={load} />}
                {order.has_inv_file && <FileSlot orderId={id} slot="inv" label="Tax Invoice (SOM-INV)" hint="" has={order.has_inv_file} filename={order.inv_filename} onChanged={load} />}
              </div>
            )}
          </div>
        </div>

      </div>
      </TabsContent>
      </Tabs>

      <Dialog open={ackOpen} onOpenChange={setAckOpen}>
        <DialogContent data-testid="order-po-ack-dialog">
          <DialogHeader><DialogTitle>Copy PO Acknowledgment Draft</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5"><Label className="text-xs">Subject</Label><Input value={ackForm.subject} onChange={(e) => setAckForm({ ...ackForm, subject: e.target.value })} data-testid="po-ack-subject" /></div>
            <div className="space-y-1.5"><Label className="text-xs">Message</Label><Textarea rows={9} value={ackForm.message} onChange={(e) => setAckForm({ ...ackForm, message: e.target.value })} data-testid="po-ack-message" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckOpen(false)}>Cancel</Button>
            <Button onClick={copyAck} data-testid="po-ack-copy" className="bg-[#1B4383] text-white hover:bg-[#16386e]"><Copy className="mr-2 h-4 w-4" />Copy Email Draft</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}

function Meta({ label, value, mono }) {
  return (
    <div>
      <div className="text-xs tracking-wide text-muted-foreground">{label}</div>
      <div className={`mt-0.5 font-medium text-slate-900 ${mono ? "font-mono text-sm" : ""}`}>{value}</div>
    </div>
  );
}
